import React, { useState, useRef } from 'react';
import './App.css';
import Webcam from 'react-webcam';
import { 
  Camera, Upload, Brain, Leaf, Play, Loader, AlertTriangle, 
  CheckCircle, Coffee, Wheat, Sprout, Sun, Globe
} from 'lucide-react';
import axios from 'axios';

const APP_NAME = 'VeraCrop.AI';

const API_URL = process.env.REACT_APP_API_URL || (
  window.location.hostname.includes('fly.dev') 
    ? 'https://cropguardai-api.fly.dev' 
    : 'http://localhost:8000'
);

// Languages
const LANGUAGES = {
  en: { name: 'English', flag: '🇺🇸' },
  pt: { name: 'Português', flag: '🇧🇷' },
  es: { name: 'Español', flag: '🇪🇸' },
  fr: { name: 'Français', flag: '🇫🇷' },
  de: { name: 'Deutsch', flag: '🇩🇪' },
  ar: { name: 'العربية', flag: '🇸🇦' },
  hi: { name: 'हिन्दी', flag: '🇮🇳' },
  ur: { name: 'اردو', flag: '🇵🇰' },
  zh: { name: '中文', flag: '🇨🇳' },
  ja: { name: '日本語', flag: '🇯🇵' },
  ko: { name: '한국어', flag: '🇰🇷' },
  ru: { name: 'Русский', flag: '🇷🇺' },
  it: { name: 'Italiano', flag: '🇮🇹' },
  nl: { name: 'Nederlands', flag: '🇳🇱' },
  tr: { name: 'Türkçe', flag: '🇹🇷' },
  vi: { name: 'Tiếng Việt', flag: '🇻🇳' }
};

// Translations
const TRANSLATIONS = {
  pt: {
    title: 'CropGuardAI',
    subtitle: 'Detecção Inteligente de Pragas e Doenças',
    tagline: 'Tecnologia Brasileira para Agricultura de Precisão',
    instruction: 'Selecione sua cultura abaixo e tire uma foto da lavoura para detectar doenças e pragas automaticamente. O sistema identifica problemas e fornece recomendações específicas para tratamento.',
    cropTypes: {
      general: 'Detecção Geral',
      soybean: 'Soja (Soybean)',
      coffee: 'Café (Coffee)',
      corn: 'Milho (Corn)',
      sugarcane: 'Cana-de-açúcar',
      citrus: 'Citros (Citrus)'
    },
    useCamera: 'Usar Câmera',
    uploadImage: 'Carregar Imagem',
    noImage: 'Nenhuma imagem selecionada',
    noImageHint: 'Carregue uma foto da lavoura ou use a câmera',
    detectDiseases: 'Detectar Doenças',
    detecting: 'Detectando...',
    completeAnalysis: 'Análise Completa',
    analyzing: 'Analisando...',
    healthy: 'SAUDÁVEL',
    severityLow: 'Severidade: BAIXA',
    severityMedium: 'Severidade: MÉDIA',
    severityHigh: 'Severidade: ALTA',
    detectedDiseases: 'Doenças Detectadas',
    recommendations: 'Recomendações',
    confidence: 'Confiança',
    footerText: 'Tecnologia Brasileira para Agricultura de Precisão',
    footerCredits: 'Desenvolvido para agricultores e consultores agrônomos'
  },
  en: {
    title: 'CropGuardAI',
    subtitle: 'Intelligent Pest and Disease Detection',
    tagline: 'Brazilian Technology for Precision Agriculture',
    instruction: 'Select your crop below and take a photo of the field to automatically detect diseases and pests. The system identifies problems and provides specific treatment recommendations.',
    cropTypes: {
      general: 'General Detection',
      soybean: 'Soybean',
      coffee: 'Coffee',
      corn: 'Corn',
      sugarcane: 'Sugarcane',
      citrus: 'Citrus'
    },
    useCamera: 'Use Camera',
    uploadImage: 'Upload Image',
    noImage: 'No image selected',
    noImageHint: 'Upload a field photo or use the camera',
    detectDiseases: 'Detect Diseases',
    detecting: 'Detecting...',
    completeAnalysis: 'Complete Analysis',
    analyzing: 'Analyzing...',
    healthy: 'HEALTHY',
    severityLow: 'Severity: LOW',
    severityMedium: 'Severity: MEDIUM',
    severityHigh: 'Severity: HIGH',
    detectedDiseases: 'Detected Diseases',
    recommendations: 'Recommendations',
    confidence: 'Confidence',
    footerText: 'Brazilian Technology for Precision Agriculture',
    footerCredits: 'Developed for farmers and agronomists'
  },
  es: {
    title: 'CropGuardAI',
    subtitle: 'Detección Inteligente de Plagas y Enfermedades',
    tagline: 'Tecnología Brasileña para Agricultura de Precisión',
    instruction: 'Seleccione su cultivo a continuación y tome una foto del campo para detectar enfermedades y plagas automáticamente. El sistema identifica problemas y proporciona recomendaciones de tratamiento específicas.',
    cropTypes: {
      general: 'Detección General',
      soybean: 'Soja',
      coffee: 'Café',
      corn: 'Maíz',
      sugarcane: 'Caña de Azúcar',
      citrus: 'Cítricos'
    },
    useCamera: 'Usar Cámara',
    uploadImage: 'Subir Imagen',
    noImage: 'Ninguna imagen seleccionada',
    noImageHint: 'Cargue una foto del campo o use la cámara',
    detectDiseases: 'Detectar Enfermedades',
    detecting: 'Detectando...',
    completeAnalysis: 'Análisis Completo',
    analyzing: 'Analizando...',
    healthy: 'SALUDABLE',
    severityLow: 'Severidad: BAJA',
    severityMedium: 'Severidad: MEDIA',
    severityHigh: 'Severidad: ALTA',
    detectedDiseases: 'Enfermedades Detectadas',
    recommendations: 'Recomendaciones',
    confidence: 'Confianza',
    footerText: 'Tecnología Brasileña para Agricultura de Precisión',
    footerCredits: 'Desarrollado para agricultores y agrónomos'
  },
  fr: {
    title: 'CropGuardAI',
    subtitle: 'Détection Intelligente des Ravageurs et Maladies',
    tagline: 'Technologie Brésilienne pour l\'Agriculture de Précision',
    instruction: 'Sélectionnez votre culture ci-dessous et prenez une photo du champ pour détecter automatiquement les maladies et les ravageurs. Le système identifie les problèmes et fournit des recommandations de traitement spécifiques.',
    cropTypes: {
      general: 'Détection Générale',
      soybean: 'Soja',
      coffee: 'Café',
      corn: 'Maïs',
      sugarcane: 'Canne à Sucre',
      citrus: 'Agrumes'
    },
    useCamera: 'Utiliser la Caméra',
    uploadImage: 'Télécharger Image',
    noImage: 'Aucune image sélectionnée',
    noImageHint: 'Téléchargez une photo du champ ou utilisez la caméra',
    detectDiseases: 'Détecter les Maladies',
    detecting: 'Détection...',
    completeAnalysis: 'Analyse Complète',
    analyzing: 'Analyse...',
    healthy: 'SAIN',
    severityLow: 'Gravité: FAIBLE',
    severityMedium: 'Gravité: MOYENNE',
    severityHigh: 'Gravité: ÉLEVÉE',
    detectedDiseases: 'Maladies Détectées',
    recommendations: 'Recommandations',
    confidence: 'Confiance',
    footerText: 'Technologie Brésilienne pour l\'Agriculture de Précision',
    footerCredits: 'Développé pour les agriculteurs et agronomes'
  },
  de: {
    title: 'CropGuardAI',
    subtitle: 'Intelligente Schädlings- und Krankheitserkennung',
    tagline: 'Brasilianische Technologie für Präzisionslandwirtschaft',
    instruction: 'Wählen Sie unten Ihre Kultur aus und machen Sie ein Foto des Feldes, um Krankheiten und Schädlinge automatisch zu erkennen. Das System identifiziert Probleme und liefert spezifische Behandlungsempfehlungen.',
    cropTypes: {
      general: 'Allgemeine Erkennung',
      soybean: 'Sojabohne',
      coffee: 'Kaffee',
      corn: 'Mais',
      sugarcane: 'Zuckerrohr',
      citrus: 'Zitrusfrüchte'
    },
    useCamera: 'Kamera Verwenden',
    uploadImage: 'Bild Hochladen',
    noImage: 'Kein Bild ausgewählt',
    noImageHint: 'Laden Sie ein Feldfoto hoch oder verwenden Sie die Kamera',
    detectDiseases: 'Krankheiten Erkennen',
    detecting: 'Erkennung...',
    completeAnalysis: 'Vollständige Analyse',
    analyzing: 'Analyse...',
    healthy: 'GESUND',
    severityLow: 'Schweregrad: NIEDRIG',
    severityMedium: 'Schweregrad: MITTEL',
    severityHigh: 'Schweregrad: HOCH',
    detectedDiseases: 'Erkannte Krankheiten',
    recommendations: 'Empfehlungen',
    confidence: 'Vertrauen',
    footerText: 'Brasilianische Technologie für Präzisionslandwirtschaft',
    footerCredits: 'Entwickelt für Landwirte und Agronomen'
  },
  ar: {
    title: 'CropGuardAI',
    subtitle: 'الكشف الذكي عن الآفات والأمراض',
    tagline: 'التكنولوجيا البرازيلية للزراعة الدقيقة',
    instruction: 'اختر المحصول أدناه والتقط صورة للحقل للكشف تلقائيًا عن الأمراض والآفات. يحدد النظام المشاكل ويقدم توصيات علاج محددة.',
    cropTypes: {
      general: 'الكشف العام',
      soybean: 'فول الصويا',
      coffee: 'قهوة',
      corn: 'ذرة',
      sugarcane: 'قصب السكر',
      citrus: 'حمضيات'
    },
    useCamera: 'استخدام الكاميرا',
    uploadImage: 'تحميل صورة',
    noImage: 'لم يتم اختيار صورة',
    noImageHint: 'قم بتحميل صورة الحقل أو استخدم الكاميرا',
    detectDiseases: 'كشف الأمراض',
    detecting: 'جاري الكشف...',
    completeAnalysis: 'تحليل كامل',
    analyzing: 'جاري التحليل...',
    healthy: 'صحي',
    severityLow: 'الخطورة: منخفضة',
    severityMedium: 'الخطورة: متوسطة',
    severityHigh: 'الخطورة: عالية',
    detectedDiseases: 'الأمراض المكتشفة',
    recommendations: 'التوصيات',
    confidence: 'الثقة',
    footerText: 'التكنولوجيا البرازيلية للزراعة الدقيقة',
    footerCredits: 'تم تطويره للمزارعين والمهندسين الزراعيين'
  },
  hi: {
    title: 'CropGuardAI',
    subtitle: 'बुद्धिमान कीट और रोग पहचान',
    tagline: 'सटीक कृषि के लिए ब्राज़ीलियाई तकनीक',
    instruction: 'नीचे अपनी फसल चुनें और रोगों और कीटों का स्वचालित रूप से पता लगाने के लिए खेत की एक तस्वीर लें। सिस्टम समस्याओं की पहचान करता है और विशिष्ट उपचार सिफारिशें प्रदान करता है।',
    cropTypes: {
      general: 'सामान्य पहचान',
      soybean: 'सोयाबीन',
      coffee: 'कॉफ़ी',
      corn: 'मक्का',
      sugarcane: 'गन्ना',
      citrus: 'खट्टे फल'
    },
    useCamera: 'कैमरा उपयोग करें',
    uploadImage: 'चित्र अपलोड करें',
    noImage: 'कोई चित्र नहीं चुना गया',
    noImageHint: 'खेत की तस्वीर अपलोड करें या कैमरा उपयोग करें',
    detectDiseases: 'रोगों का पता लगाएं',
    detecting: 'पता लगाया जा रहा है...',
    completeAnalysis: 'पूर्ण विश्लेषण',
    analyzing: 'विश्लेषण किया जा रहा है...',
    healthy: 'स्वस्थ',
    severityLow: 'गंभीरता: कम',
    severityMedium: 'गंभीरता: मध्यम',
    severityHigh: 'गंभीरता: उच्च',
    detectedDiseases: 'पहचाने गए रोग',
    recommendations: 'सिफारिशें',
    confidence: 'विश्वास',
    footerText: 'सटीक कृषि के लिए ब्राज़ीलियाई तकनीक',
    footerCredits: 'किसानों और कृषि विशेषज्ञों के लिए विकसित'
  },
  ur: {
    title: 'CropGuardAI',
    subtitle: 'ذہین کیڑوں اور بیماریوں کی تشخیص',
    tagline: 'درست زراعت کے لیے برازیلی ٹیکنالوجی',
    instruction: 'ذیل میں اپنی فصل منتخب کریں اور بیماریوں اور کیڑوں کا خودکار طریقے سے پتہ لگانے کے لیے کھیت کی تصویر لیں۔ نظام مسائل کی نشاندہی کرتا ہے اور مخصوص علاج کی سفارشات فراہم کرتا ہے۔',
    cropTypes: {
      general: 'عام تشخیص',
      soybean: 'سویابین',
      coffee: 'کافی',
      corn: 'مکئی',
      sugarcane: 'گنا',
      citrus: 'کھٹے پھل'
    },
    useCamera: 'کیمرہ استعمال کریں',
    uploadImage: 'تصویر اپ لوڈ کریں',
    noImage: 'کوئی تصویر منتخب نہیں کی گئی',
    noImageHint: 'کھیت کی تصویر اپ لوڈ کریں یا کیمرہ استعمال کریں',
    detectDiseases: 'بیماریوں کا پتہ لگائیں',
    detecting: 'پتہ لگایا جا رہا ہے...',
    completeAnalysis: 'مکمل تجزیہ',
    analyzing: 'تجزیہ کیا جا رہا ہے...',
    healthy: 'صحت مند',
    severityLow: 'شدت: کم',
    severityMedium: 'شدت: درمیانی',
    severityHigh: 'شدت: زیادہ',
    detectedDiseases: 'تشخیص شدہ بیماریاں',
    recommendations: 'سفارشات',
    confidence: 'اعتماد',
    footerText: 'درست زراعت کے لیے برازیلی ٹیکنالوجی',
    footerCredits: 'کسانوں اور زرعی ماہرین کے لیے تیار کیا گیا'
  },
  zh: {
    title: 'CropGuardAI',
    subtitle: '智能害虫和疾病检测',
    tagline: '精准农业的巴西技术',
    instruction: '在下方选择您的作物并拍摄田地照片以自动检测疾病和害虫。系统识别问题并提供具体的治疗建议。',
    cropTypes: {
      general: '常规检测',
      soybean: '大豆',
      coffee: '咖啡',
      corn: '玉米',
      sugarcane: '甘蔗',
      citrus: '柑橘'
    },
    useCamera: '使用相机',
    uploadImage: '上传图片',
    noImage: '未选择图片',
    noImageHint: '上传田地照片或使用相机',
    detectDiseases: '检测疾病',
    detecting: '检测中...',
    completeAnalysis: '完整分析',
    analyzing: '分析中...',
    healthy: '健康',
    severityLow: '严重程度：低',
    severityMedium: '严重程度：中',
    severityHigh: '严重程度：高',
    detectedDiseases: '检测到的疾病',
    recommendations: '建议',
    confidence: '置信度',
    footerText: '精准农业的巴西技术',
    footerCredits: '为农民和农学家开发'
  },
  ja: {
    title: 'CropGuardAI',
    subtitle: 'インテリジェントな害虫・病害検出',
    tagline: '精密農業のためのブラジル技術',
    instruction: '下の作物を選択し、畑の写真を撮って病気や害虫を自動的に検出します。システムは問題を特定し、具体的な治療の推奨事項を提供します。',
    cropTypes: {
      general: '一般検出',
      soybean: '大豆',
      coffee: 'コーヒー',
      corn: 'トウモロコシ',
      sugarcane: 'サトウキビ',
      citrus: '柑橘類'
    },
    useCamera: 'カメラを使用',
    uploadImage: '画像をアップロード',
    noImage: '画像が選択されていません',
    noImageHint: '畑の写真をアップロードするか、カメラを使用してください',
    detectDiseases: '病害を検出',
    detecting: '検出中...',
    completeAnalysis: '完全な分析',
    analyzing: '分析中...',
    healthy: '健康',
    severityLow: '重症度：低',
    severityMedium: '重症度：中',
    severityHigh: '重症度：高',
    detectedDiseases: '検出された病害',
    recommendations: '推奨事項',
    confidence: '信頼度',
    footerText: '精密農業のためのブラジル技術',
    footerCredits: '農家と農学者のために開発'
  },
  ko: {
    title: 'CropGuardAI',
    subtitle: '지능형 해충 및 질병 탐지',
    tagline: '정밀 농업을 위한 브라질 기술',
    instruction: '아래에서 작물을 선택하고 밭 사진을 찍어 질병과 해충을 자동으로 감지합니다. 시스템은 문제를 식별하고 구체적인 치료 권장 사항을 제공합니다.',
    cropTypes: {
      general: '일반 탐지',
      soybean: '대두',
      coffee: '커피',
      corn: '옥수수',
      sugarcane: '사탕수수',
      citrus: '감귤류'
    },
    useCamera: '카메라 사용',
    uploadImage: '이미지 업로드',
    noImage: '이미지가 선택되지 않음',
    noImageHint: '밭 사진을 업로드하거나 카메라를 사용하세요',
    detectDiseases: '질병 탐지',
    detecting: '탐지 중...',
    completeAnalysis: '전체 분석',
    analyzing: '분석 중...',
    healthy: '건강',
    severityLow: '심각도: 낮음',
    severityMedium: '심각도: 중간',
    severityHigh: '심각도: 높음',
    detectedDiseases: '탐지된 질병',
    recommendations: '권장 사항',
    confidence: '신뢰도',
    footerText: '정밀 농업을 위한 브라질 기술',
    footerCredits: '농부와 농학자를 위해 개발됨'
  },
  ru: {
    title: 'CropGuardAI',
    subtitle: 'Интеллектуальное обнаружение вредителей и болезней',
    tagline: 'Бразильская технология для точного земледелия',
    instruction: 'Выберите культуру ниже и сделайте фото поля для автоматического обнаружения болезней и вредителей. Система выявляет проблемы и предоставляет конкретные рекомендации по лечению.',
    cropTypes: {
      general: 'Общее обнаружение',
      soybean: 'Соя',
      coffee: 'Кофе',
      corn: 'Кукуруза',
      sugarcane: 'Сахарный тростник',
      citrus: 'Цитрусовые'
    },
    useCamera: 'Использовать камеру',
    uploadImage: 'Загрузить изображение',
    noImage: 'Изображение не выбрано',
    noImageHint: 'Загрузите фото поля или используйте камеру',
    detectDiseases: 'Обнаружить болезни',
    detecting: 'Обнаружение...',
    completeAnalysis: 'Полный анализ',
    analyzing: 'Анализ...',
    healthy: 'ЗДОРОВЫЙ',
    severityLow: 'Серьезность: НИЗКАЯ',
    severityMedium: 'Серьезность: СРЕДНЯЯ',
    severityHigh: 'Серьезность: ВЫСОКАЯ',
    detectedDiseases: 'Обнаруженные болезни',
    recommendations: 'Рекомендации',
    confidence: 'Достоверность',
    footerText: 'Бразильская технология для точного земледелия',
    footerCredits: 'Разработано для фермеров и агрономов'
  },
  it: {
    title: 'CropGuardAI',
    subtitle: 'Rilevamento Intelligente di Parassiti e Malattie',
    tagline: 'Tecnologia Brasiliana per l\'Agricoltura di Precisione',
    instruction: 'Seleziona la tua coltura qui sotto e scatta una foto del campo per rilevare automaticamente malattie e parassiti. Il sistema identifica i problemi e fornisce raccomandazioni di trattamento specifiche.',
    cropTypes: {
      general: 'Rilevamento Generale',
      soybean: 'Soia',
      coffee: 'Caffè',
      corn: 'Mais',
      sugarcane: 'Canna da Zucchero',
      citrus: 'Agrumi'
    },
    useCamera: 'Usa Fotocamera',
    uploadImage: 'Carica Immagine',
    noImage: 'Nessuna immagine selezionata',
    noImageHint: 'Carica una foto del campo o usa la fotocamera',
    detectDiseases: 'Rileva Malattie',
    detecting: 'Rilevamento...',
    completeAnalysis: 'Analisi Completa',
    analyzing: 'Analisi...',
    healthy: 'SANO',
    severityLow: 'Gravità: BASSA',
    severityMedium: 'Gravità: MEDIA',
    severityHigh: 'Gravità: ALTA',
    detectedDiseases: 'Malattie Rilevate',
    recommendations: 'Raccomandazioni',
    confidence: 'Confidenza',
    footerText: 'Tecnologia Brasiliana per l\'Agricoltura di Precisione',
    footerCredits: 'Sviluppato per agricoltori e agronomi'
  },
  nl: {
    title: 'CropGuardAI',
    subtitle: 'Intelligente Plaag- en Ziektedetectie',
    tagline: 'Braziliaanse Technologie voor Precisie Landbouw',
    instruction: 'Selecteer hieronder uw gewas en maak een foto van het veld om automatisch ziekten en plagen te detecteren. Het systeem identificeert problemen en geeft specifieke behandelingsaanbevelingen.',
    cropTypes: {
      general: 'Algemene Detectie',
      soybean: 'Sojabonen',
      coffee: 'Koffie',
      corn: 'Maïs',
      sugarcane: 'Suikerriet',
      citrus: 'Citrusvruchten'
    },
    useCamera: 'Camera Gebruiken',
    uploadImage: 'Afbeelding Uploaden',
    noImage: 'Geen afbeelding geselecteerd',
    noImageHint: 'Upload een foto van het veld of gebruik de camera',
    detectDiseases: 'Ziekten Detecteren',
    detecting: 'Detecteren...',
    completeAnalysis: 'Volledige Analyse',
    analyzing: 'Analyseren...',
    healthy: 'GEZOND',
    severityLow: 'Ernst: LAAG',
    severityMedium: 'Ernst: GEMIDDELD',
    severityHigh: 'Ernst: HOOG',
    detectedDiseases: 'Gedetecteerde Ziekten',
    recommendations: 'Aanbevelingen',
    confidence: 'Vertrouwen',
    footerText: 'Braziliaanse Technologie voor Precisie Landbouw',
    footerCredits: 'Ontwikkeld voor boeren en agronomen'
  },
  tr: {
    title: 'CropGuardAI',
    subtitle: 'Akıllı Haşere ve Hastalık Tespiti',
    tagline: 'Hassas Tarım için Brezilya Teknolojisi',
    instruction: 'Aşağıdan ürününüzü seçin ve hastalıkları ve zararlıları otomatik olarak tespit etmek için tarladan bir fotoğraf çekin. Sistem sorunları tanımlar ve özel tedavi önerileri sunar.',
    cropTypes: {
      general: 'Genel Tespit',
      soybean: 'Soya Fasulyesi',
      coffee: 'Kahve',
      corn: 'Mısır',
      sugarcane: 'Şeker Kamışı',
      citrus: 'Narenciye'
    },
    useCamera: 'Kamera Kullan',
    uploadImage: 'Resim Yükle',
    noImage: 'Resim seçilmedi',
    noImageHint: 'Tarla fotoğrafı yükleyin veya kamerayı kullanın',
    detectDiseases: 'Hastalıkları Tespit Et',
    detecting: 'Tespit ediliyor...',
    completeAnalysis: 'Tam Analiz',
    analyzing: 'Analiz ediliyor...',
    healthy: 'SAĞLIKLI',
    severityLow: 'Şiddet: DÜŞÜK',
    severityMedium: 'Şiddet: ORTA',
    severityHigh: 'Şiddet: YÜKSEK',
    detectedDiseases: 'Tespit Edilen Hastalıklar',
    recommendations: 'Öneriler',
    confidence: 'Güven',
    footerText: 'Hassas Tarım için Brezilya Teknolojisi',
    footerCredits: 'Çiftçiler ve tarım uzmanları için geliştirilmiştir'
  },
  vi: {
    title: 'CropGuardAI',
    subtitle: 'Phát Hiện Sâu Bệnh Thông Minh',
    tagline: 'Công Nghệ Brazil cho Nông Nghiệp Chính Xác',
    instruction: 'Chọn cây trồng của bạn bên dưới và chụp ảnh cánh đồng để tự động phát hiện bệnh và sâu hại. Hệ thống xác định vấn đề và cung cấp các khuyến nghị điều trị cụ thể.',
    cropTypes: {
      general: 'Phát Hiện Chung',
      soybean: 'Đậu Nành',
      coffee: 'Cà Phê',
      corn: 'Ngô',
      sugarcane: 'Mía',
      citrus: 'Cam Quýt'
    },
    useCamera: 'Sử Dụng Máy Ảnh',
    uploadImage: 'Tải Ảnh Lên',
    noImage: 'Chưa chọn hình ảnh',
    noImageHint: 'Tải ảnh cánh đồng lên hoặc sử dụng máy ảnh',
    detectDiseases: 'Phát Hiện Bệnh',
    detecting: 'Đang phát hiện...',
    completeAnalysis: 'Phân Tích Hoàn Chỉnh',
    analyzing: 'Đang phân tích...',
    healthy: 'KHỎE MẠNH',
    severityLow: 'Mức độ nghiêm trọng: THẤP',
    severityMedium: 'Mức độ nghiêm trọng: TRUNG BÌNH',
    severityHigh: 'Mức độ nghiêm trọng: CAO',
    detectedDiseases: 'Bệnh Được Phát Hiện',
    recommendations: 'Khuyến Nghị',
    confidence: 'Độ Tin Cậy',
    footerText: 'Công Nghệ Brazil cho Nông Nghiệp Chính Xác',
    footerCredits: 'Được phát triển cho nông dân và nhà nông học'
  }
};

// Crop Types (icons remain the same)
const CROP_TYPES = {
  general: { icon: Brain, color: '#006994', emoji: '🌾' },
  soybean: { icon: Leaf, color: '#006994', emoji: '🫘' },
  coffee: { icon: Coffee, color: '#003153', emoji: '☕' },
  corn: { icon: Wheat, color: '#1c3975', emoji: '🌽' },
  sugarcane: { icon: Sprout, color: '#006994', emoji: '🎋' },
  citrus: { icon: Sun, color: '#87CEEB', emoji: '🍊' }
};

function App() {
  const [language, setLanguage] = useState('en');
  const [cropType, setCropType] = useState('general');
  const [image, setImage] = useState(null);
  const [diseases, setDiseases] = useState([]);
  const [analysis, setAnalysis] = useState('');
  const [loading, setLoading] = useState(false);
  const [useWebcam, setUseWebcam] = useState(false);
  const [severity, setSeverity] = useState('');
  const [recommendations, setRecommendations] = useState([]);
  const [facingMode, setFacingMode] = useState('environment');
  const webcamRef = useRef(null);
  const fileInputRef = useRef(null);
  const canvasRef = useRef(null);
  const clickableCirclesRef = useRef([]);
  
  // Get current translations
  const t = TRANSLATIONS[language] || TRANSLATIONS.pt;

  // Clear results when crop type changes
  const handleCropChange = (newCrop) => {
    setCropType(newCrop);
    setDiseases([]);
    setAnalysis('');
    setSeverity('');
    setRecommendations([]);
    if (canvasRef.current) {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      canvas.width = 0;
      canvas.height = 0;
    }
  };

  const captureImage = () => {
    const imageSrc = webcamRef.current.getScreenshot();
    
    // Compress captured image
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      
      // Reduce size for faster processing (max 1280px)
      let width = img.width;
      let height = img.height;
      const maxSize = 1280;
      
      if (width > maxSize || height > maxSize) {
        if (width > height) {
          height = (height / width) * maxSize;
          width = maxSize;
        } else {
          width = (width / height) * maxSize;
          height = maxSize;
        }
      }
      
      canvas.width = width;
      canvas.height = height;
      ctx.drawImage(img, 0, 0, width, height);
      
      // Compress to 75% quality for faster upload/processing
      const compressedImage = canvas.toDataURL('image/jpeg', 0.75);
      setImage(compressedImage);
    };
    img.src = imageSrc;
    
    setUseWebcam(false);
    setDiseases([]);
    setAnalysis('');
    setSeverity('');
    setRecommendations([]);
    if (canvasRef.current) {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      canvas.width = 0;
      canvas.height = 0;
    }
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Compress and resize image before uploading
      const reader = new FileReader();
      reader.onloadend = () => {
        const img = new Image();
        img.onload = () => {
          // Create canvas for resizing
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          
          // Calculate new dimensions (max 1280px for faster processing)
          let width = img.width;
          let height = img.height;
          const maxSize = 1280;
          
          if (width > maxSize || height > maxSize) {
            if (width > height) {
              height = (height / width) * maxSize;
              width = maxSize;
            } else {
              width = (width / height) * maxSize;
              height = maxSize;
            }
          }
          
          canvas.width = width;
          canvas.height = height;
          
          // Draw and compress image (0.75 quality for faster upload/processing)
          ctx.drawImage(img, 0, 0, width, height);
          const compressedImage = canvas.toDataURL('image/jpeg', 0.75);
          
          setImage(compressedImage);
          setDiseases([]);
          setAnalysis('');
          setSeverity('');
          setRecommendations([]);
          if (canvasRef.current) {
            const resultCanvas = canvasRef.current;
            const resultCtx = resultCanvas.getContext('2d');
            resultCtx.clearRect(0, 0, resultCanvas.width, resultCanvas.height);
            resultCanvas.width = 0;
            resultCanvas.height = 0;
          }
        };
        img.src = reader.result;
      };
      reader.readAsDataURL(file);
    }
  };

  const detectDiseases = async () => {
    if (!image) return;
    
    setLoading(true);
    setDiseases([]);
    setSeverity('');
    setRecommendations([]);
    
    try {
      const formData = new FormData();
      formData.append('crop_type', cropType);
      formData.append('max_items', '10');
      formData.append('language', language);
      
      // Convert base64 to file
      const response = await fetch(image);
      const blob = await response.blob();
      formData.append('image', blob, 'crop.jpg');
      
      const result = await axios.post(`${API_URL}/api/detect`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 120000 // 120 seconds timeout for image processing
      });
      
      if (result.data.success) {
        setDiseases(result.data.diseases);
        setSeverity(result.data.severity_level);
        setRecommendations(result.data.recommendations);
        drawDiseasesOnCanvas(result.data.diseases);
      }
    } catch (error) {
      console.error('Error detecting diseases:', error);
      const errorMsg = error.response?.data?.detail || error.message;
      
      // Show user-friendly error message
      if (errorMsg.includes('quota') || errorMsg.includes('rate')) {
        alert('⚠️ Limite de API atingido. Por favor, aguarde alguns segundos e tente novamente.');
      } else if (errorMsg.includes('timeout')) {
        alert('⚠️ Tempo esgotado. Por favor, tente com uma imagem menor.');
      } else {
        alert('Erro ao detectar doenças: ' + errorMsg + '\n\nTente novamente em alguns segundos.');
      }
    } finally {
      setLoading(false);
    }
  };

  const analyzeHealth = async () => {
    if (!image) return;
    
    setLoading(true);
    setAnalysis('');
    
    try {
      const formData = new FormData();
      formData.append('crop_type', cropType);
      formData.append('language', language);
      
      const response = await fetch(image);
      const blob = await response.blob();
      formData.append('image', blob, 'crop.jpg');
      
      const result = await axios.post(`${API_URL}/api/analyze`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 120000 // 120 seconds timeout for image processing
      });
      
      if (result.data.success) {
        setAnalysis(result.data.analysis);
      }
    } catch (error) {
      console.error('Error analyzing crop:', error);
      alert('Erro ao analisar cultura: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const drawDiseasesOnCanvas = (detectedDiseases) => {
    if (!canvasRef.current || !image) return;
    
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const img = new Image();
    
    img.onload = () => {
      canvas.width = img.width;
      canvas.height = img.height;
      ctx.drawImage(img, 0, 0, img.width, img.height);
      
      clickableCirclesRef.current = [];
      
      const baseSize = Math.min(img.width, img.height);
      const circleRadius = Math.max(8, baseSize * 0.012); // Moderate size for desktop
      const fontSize = Math.max(12, baseSize * 0.015); // Readable text size
      const labelOffset = circleRadius * 2;
      
      detectedDiseases.forEach((disease) => {
        const [y, x] = disease.point;
        const actualX = (x / 1000) * img.width;
        const actualY = (y / 1000) * img.height;
        
        // Severity-based colors
        let color = '#2ecc71'; // default green for low
        if (disease.severity === 'high') color = '#e74c3c'; // red
        else if (disease.severity === 'medium') color = '#FFD700'; // Brazilian flag gold
        
        clickableCirclesRef.current.push({
          x: actualX,
          y: actualY,
          radius: circleRadius,
          label: disease.label,
          severity: disease.severity
        });
        
        // Draw circle (no white ring)
        ctx.beginPath();
        ctx.arc(actualX, actualY, circleRadius, 0, 2 * Math.PI);
        ctx.fillStyle = color;
        ctx.fill();
        
        // Draw pulsing animation border for high severity
        if (disease.severity === 'high') {
          ctx.beginPath();
          ctx.arc(actualX, actualY, circleRadius * 1.5, 0, 2 * Math.PI);
          ctx.strokeStyle = 'rgba(231, 76, 60, 0.5)';
          ctx.lineWidth = Math.max(2, circleRadius * 0.15);
          ctx.stroke();
        }
        
        // Draw label (NO BACKGROUND - just text with outline)
        ctx.font = `bold ${fontSize}px Arial`;
        let labelText = disease.label;
        
        // Truncate long text to avoid overflow
        const maxLength = 35;
        if (labelText.length > maxLength) {
          labelText = labelText.substring(0, maxLength) + '...';
        }
        
        const labelWidth = ctx.measureText(labelText).width;
        
        // Ensure label stays within image bounds
        let labelX = actualX - (labelWidth / 2);
        const labelY = actualY + labelOffset;
        
        // Adjust X position if too close to edges
        if (labelX < 5) labelX = 5;
        if (labelX + labelWidth > img.width - 5) labelX = img.width - labelWidth - 5;
        
        // Text color based on severity
        let textColor = '#2ecc71'; // green for low
        if (disease.severity === 'high') textColor = '#e74c3c'; // red
        else if (disease.severity === 'medium') textColor = '#FFD700'; // gold
        
        // Draw text with strong outline for visibility
        ctx.strokeStyle = '#000';
        ctx.lineWidth = Math.max(2, fontSize * 0.25);
        ctx.strokeText(labelText, labelX, labelY);
        
        ctx.fillStyle = textColor;
        ctx.fillText(labelText, labelX, labelY);
      });
    };
    
    img.src = image;
  };

  const CropIcon = CROP_TYPES[cropType]?.icon || Leaf;

  return (
    <div className="App">
      <header className="App-header">
        <div className="language-selector-wrapper">
          <Globe size={22} className="language-icon" strokeWidth={2.5} />
          <select 
            className="language-selector"
            value={language}
            onChange={(e) => setLanguage(e.target.value)}
            aria-label="Select Language"
          >
            {Object.entries(LANGUAGES).map(([code, lang]) => (
              <option key={code} value={code}>
                {lang.flag} {lang.name}
              </option>
            ))}
          </select>
        </div>
        <div className="header-content">
          <Leaf size={45} className="logo" />
          <h1>🌾 {APP_NAME}</h1>
          <p>{t.subtitle}</p>
          <p className="subtitle">{t.tagline}</p>
        </div>
      </header>

      {/* Instruction Banner */}
      <div className="instruction-banner">
        <span className="banner-icon">💡</span>
        <p>{t.instruction}</p>
      </div>

      <div className="container">
        <div className="crop-selector">
          {/* Show General first, then others */}
          {['general', 'soybean', 'coffee', 'corn', 'sugarcane', 'citrus'].map((key) => {
            const config = CROP_TYPES[key];
            const Icon = config.icon;
            return (
              <button 
                key={key}
                className={`crop-btn ${cropType === key ? 'active' : ''}`}
                onClick={() => handleCropChange(key)}
              >
                <span className="crop-emoji">{config.emoji}</span>
                <Icon size={20} />
                {t.cropTypes[key]}
              </button>
            );
          })}
        </div>

        <div className="content-grid">
          <div className="image-section">
            <div className="image-controls">
              <button onClick={() => setUseWebcam(!useWebcam)} className="control-btn">
                <Camera size={18} />
                {useWebcam ? (language === 'pt' ? 'Esconder Câmera' : 'Hide Camera') : t.useCamera}
              </button>
              <button onClick={() => fileInputRef.current?.click()} className="control-btn">
                <Upload size={18} />
                {t.uploadImage}
              </button>
              <input 
                ref={fileInputRef}
                type="file" 
                accept="image/*" 
                onChange={handleFileUpload}
                style={{ display: 'none' }}
              />
            </div>

            {useWebcam && (
              <div className="webcam-container">
                <Webcam
                  ref={webcamRef}
                  screenshotFormat="image/jpeg"
                  className="webcam"
                  videoConstraints={{ facingMode: facingMode }}
                />
                <div className="webcam-controls">
                  <button 
                    onClick={() => setFacingMode(facingMode === 'user' ? 'environment' : 'user')} 
                    className="flip-camera-btn"
                  >
                    🔄 Virar Câmera
                  </button>
                  <button onClick={captureImage} className="capture-btn">
                    📸 Capturar
                  </button>
                </div>
              </div>
            )}

            {image && !useWebcam && (
              <div className="image-display">
                {diseases.length === 0 && (
                  <img src={image} alt="Crop" />
                )}
                <canvas 
                  ref={canvasRef} 
                  className="result-canvas" 
                  style={{ display: diseases.length > 0 ? 'block' : 'none' }} 
                />
              </div>
            )}

            {!image && !useWebcam && (
              <div className="placeholder">
                <CropIcon size={80} style={{ color: '#006994' }} />
                <p>{t.noImage}</p>
                <p className="placeholder-hint">{t.noImageHint}</p>
              </div>
            )}
          </div>

          <div className="results-section">
            <div className="crop-content">
              <h2>
                <CropIcon size={28} style={{ color: '#006994' }} />
                {t.cropTypes[cropType]}
              </h2>
              
              <div className="action-buttons">
                <button
                  onClick={detectDiseases}
                  disabled={!image || loading}
                  className="action-btn"
                >
                  {loading ? <Loader className="spin" size={20} /> : <Play size={20} />}
                  {loading ? t.detecting : t.detectDiseases}
                </button>
                
                <button
                  onClick={analyzeHealth}
                  disabled={!image || loading}
                  className="action-btn secondary"
                >
                  {loading ? <Loader className="spin" size={20} /> : <Brain size={20} />}
                  {loading ? t.analyzing : t.completeAnalysis}
                </button>
              </div>

              {severity && (
                <div className={`severity-badge severity-${severity}`}>
                  {severity === 'high' && <AlertTriangle size={20} />}
                  {severity === 'medium' && <AlertTriangle size={20} />}
                  {severity === 'low' && <CheckCircle size={20} />}
                  <span>
                    {severity === 'low' ? (
                      <><strong>✓ {t.healthy}</strong></>
                    ) : (
                      <>{severity === 'high' ? t.severityHigh : t.severityMedium}</>
                    )}
                  </span>
                </div>
              )}
              
              {diseases.length > 0 && (
                <div className="results">
                  <h3>🐛 {t.detectedDiseases} ({diseases.length})</h3>
                  <div className="diseases-list">
                    {diseases.map((disease, idx) => (
                      <div 
                        key={idx} 
                        className={`disease-item severity-${disease.severity}`}
                      >
                        <div className="disease-header">
                          <span className="disease-label">
                            {disease.severity === 'high' && '🔴'}
                            {disease.severity === 'medium' && '🟡'}
                            {disease.severity === 'low' && '✓'}
                            {' '}{disease.label}
                          </span>
                          {disease.confidence && (
                            <span className="disease-confidence">
                              {(disease.confidence * 100).toFixed(0)}%
                            </span>
                          )}
                        </div>
                        {disease.severity && (
                          <span className="disease-severity">
                            {disease.severity === 'low' ? 'Condição: Saudável' : 
                             `Severidade: ${disease.severity === 'high' ? 'Alta' : 'Média'}`}
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {recommendations.length > 0 && (
                <div className="results recommendations">
                  <h3>💊 {t.recommendations}</h3>
                  <ul className="recommendations-list">
                    {recommendations.map((rec, idx) => (
                      <li key={idx}>{rec}</li>
                    ))}
                  </ul>
                  <p className="recommendation-note">
                    <strong>Nota:</strong> Consulte sempre um agrônomo para um plano de tratamento detalhado.
                  </p>
                </div>
              )}

              {analysis && (
                <div className="results">
                  <h3>📊 {t.completeAnalysis}</h3>
                  <div className="analysis-content">
                    {analysis}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <footer className="App-footer">
        <p><strong>{APP_NAME}</strong> • {t.footerText} 🇧🇷</p>
        <p className="footer-credits">{t.footerCredits}</p>
      </footer>
    </div>
  );
}

export default App;

